import React, { useEffect, useState } from 'react'
import { useMutation, gql } from '@apollo/client'
import { Box, Button, InputAdornment, MenuItem, TextField, Typography } from '@mui/material'

// What RushMyFood charges a restaurant for each of its manual deliveries that
// RushMyFood riders or First Delivery carry (backend utils/manualDelivery.js).
export const MANUAL_DELIVERY_FEE_SETTINGS = gql`
  query ManualDeliveryFeeSettings {
    manualDeliveryFeeSettings { restaurantId mode flat base perMile includedMiles summary }
  }
`
const SET_MANUAL_DELIVERY_FEE = gql`
  mutation SetManualDeliveryFee($restaurantId: ID!, $mode: String!, $flat: Float, $base: Float, $perMile: Float, $includedMiles: Float) {
    setManualDeliveryFee(restaurantId: $restaurantId, mode: $mode, flat: $flat, base: $base, perMile: $perMile, includedMiles: $includedMiles) {
      restaurantId mode flat base perMile includedMiles summary
    }
  }
`

const MODES = [
  { value: 'ADDRESS', label: 'Normal fee for the address' },
  { value: 'FLAT', label: 'Flat fee' },
  { value: 'PER_MILE', label: 'Base + per mile' },
]

const label = { fontSize: '0.68rem', fontWeight: 700, color: '#64748b', mb: 0.4, textTransform: 'uppercase', letterSpacing: '0.04em' }
const input = { borderRadius: '10px', fontSize: '0.9rem', bgcolor: '#fff' }
const outline = { '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#e2e8f0' }, '&:hover fieldset': { borderColor: '#93c5fd' }, '&.Mui-focused fieldset': { borderColor: '#3b82f6' } } }
const str = v => (v == null ? '' : String(v))

function Money({ id, title, value, onChange, width = 110, suffix }) {
  return (
    <Box>
      <Typography sx={label}>{title}</Typography>
      <TextField
        id={id}
        type="number"
        size="small"
        value={value}
        onChange={e => onChange(e.target.value)}
        inputProps={{ min: 0, step: 0.25 }}
        InputProps={{
          ...(suffix
            ? { endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.8rem', color: '#94a3b8' }}>{suffix}</Typography></InputAdornment> }
            : { startAdornment: <InputAdornment position="start"><Typography sx={{ fontSize: '0.85rem', color: '#94a3b8' }}>$</Typography></InputAdornment> }),
          sx: { ...input, width },
        }}
        sx={outline}
      />
    </Box>
  )
}

/** One restaurant's manual delivery fee, on the Commission page. */
export default function ManualDeliveryFeeRow({ restaurantId, setting, onNotify }) {
  const [mode, setMode] = useState('ADDRESS')
  const [flat, setFlat] = useState('')
  const [base, setBase] = useState('')
  const [perMile, setPerMile] = useState('')
  const [included, setIncluded] = useState('')
  const [save, { loading }] = useMutation(SET_MANUAL_DELIVERY_FEE, { refetchQueries: [{ query: MANUAL_DELIVERY_FEE_SETTINGS }] })

  useEffect(() => {
    setMode(setting?.mode || 'ADDRESS')
    setFlat(str(setting?.flat))
    setBase(str(setting?.base))
    setPerMile(str(setting?.perMile))
    setIncluded(str(setting?.includedMiles))
  }, [setting])

  const onSave = () => {
    const n = v => (v === '' ? null : Number(v))
    save({
      variables: { restaurantId, mode, flat: n(flat), base: n(base), perMile: n(perMile), includedMiles: n(included) },
      onCompleted: d => onNotify(restaurantId, 'success', `Manual delivery fee: ${d.setManualDeliveryFee.summary}`),
      onError: e => onNotify(restaurantId, 'error', e.graphQLErrors?.[0]?.message || 'Could not save the manual delivery fee'),
    })
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', alignItems: 'flex-end', gap: 1.5, flexWrap: 'wrap', pt: 1.5, borderTop: '1px dashed #e2e8f0' }}>
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#334155' }}>Manual delivery fee</Typography>
        <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>Charged when RushMyFood delivers an order the restaurant typed in, plus the tip</Typography>
      </Box>
      <Box>
        <Typography sx={label}>Fee</Typography>
        <TextField
          select
          size="small"
          value={mode}
          onChange={e => setMode(e.target.value)}
          InputProps={{ sx: { ...input, width: 230 } }}
          sx={outline}
        >
          {MODES.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
        </TextField>
      </Box>
      {mode === 'FLAT' && <Money id={`${restaurantId}-manual-flat`} title="Per delivery" value={flat} onChange={setFlat} />}
      {mode === 'PER_MILE' && (
        <>
          <Money id={`${restaurantId}-manual-base`} title="Base" value={base} onChange={setBase} />
          <Money id={`${restaurantId}-manual-mile`} title="Per mile" value={perMile} onChange={setPerMile} />
          <Money id={`${restaurantId}-manual-included`} title="Miles included" value={included} onChange={setIncluded} suffix="mi" width={120} />
        </>
      )}
      <Button
        variant="outlined"
        size="small"
        disabled={loading}
        onClick={onSave}
        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 2.5, py: 0.9, fontSize: '0.8rem', borderColor: '#93c5fd', color: '#2563eb' }}
      >
        {loading ? 'Saving…' : 'Save fee'}
      </Button>
    </Box>
  )
}
